// IntentBadgeRow — Phase 9 follow-up (backlog #19) + Polish #13 sentiment.
//
// Renders right under the work-head eyebrow on the Center page so Kimi
// sees, at the top of the thread:
//   - what the classifier thinks the customer is asking about
//     (sky "Where's my order?" / clay "Damage" / etc), with the trigger
//     phrase as a tooltip
//   - the sentiment of the latest inbound message (happy / anxious /
//     annoyed / heated / calm) — Polish #13
//   - the order number the classifier extracted from the body
//     (e.g. MD-12345 or #67890) as a clickable chip that copies to the
//     clipboard so Kimi can paste it into Shopify / ShipStation
//
// Falls back silently if the classifier returned 'generic' or if there's
// no order number in the body. Sentiment chip also silent on 'calm'
// (the default) so the row only shouts when there's something worth
// noticing.

import { useState } from 'react';
import { useEnsureIntents, FALLBACK_INTENT_META } from '@/lib/hooks/useEnsureIntents';
import { useConversationSentiment } from '@/lib/hooks/useConversationSentiment';
import { ToneChip } from '@/components/center/ToneChip';

export interface IntentBadgeRowProps {
  conversationId: string;
}

export function IntentBadgeRow({ conversationId }: IntentBadgeRowProps) {
  const q = useEnsureIntents([conversationId]);
  const sentimentQ = useConversationSentiment(conversationId);
  const [copied, setCopied] = useState(false);

  // We keep rendering while sentiment is loading — the intent + order
  // chips shouldn't wait on the Haiku tone call.
  if (q.isLoading) return null;
  const entry = q.data?.intents[conversationId];
  const meta = q.data?.meta ?? FALLBACK_INTENT_META;

  // Sentiment: only surface when it's notable. "calm" is the default
  // and would just add noise. Loading / error → hidden chip.
  const sentimentTone = sentimentQ.data?.tone ?? null;
  const showSentiment = sentimentTone !== null && sentimentTone !== 'calm';

  if (!entry && !showSentiment) return null;

  const showIntent = !!entry && entry.intent !== 'generic';
  const showOrder = !!entry?.order_number_hint;
  if (!showIntent && !showOrder && !showSentiment) return null;

  const intentMeta = entry ? meta[entry.intent] : undefined;
  const evidence = entry?.evidence_phrase ?? intentMeta?.label ?? '';

  const onCopyOrder = async () => {
    if (!entry?.order_number_hint) return;
    try {
      await navigator.clipboard.writeText(entry.order_number_hint);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      // Permissions or insecure context — silently no-op.
    }
  };

  return (
    <div className="intent-badge-row" aria-label="Detected intent">
      {showIntent && intentMeta ? (
        <span
          className={`intent-chip ${intentMeta.chip}`}
          title={evidence ? `Triggered by: "${evidence}"` : intentMeta.label}
        >
          {intentMeta.label}
        </span>
      ) : null}
      {showSentiment ? <ToneChip tone={sentimentTone} /> : null}
      {showOrder && entry ? (
        <button
          type="button"
          className="order-hint-chip"
          onClick={onCopyOrder}
          title="Click to copy — paste into Shopify or ShipStation order search"
        >
          {/* tiny clipboard svg, inherits currentColor */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={11} height={11}>
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          {copied ? 'Copied' : entry.order_number_hint}
        </button>
      ) : null}
    </div>
  );
}
